import { API_BASE_URL } from "../configuration/config";
import { BaseService } from "./base-service";
import { StatusCodes } from "http-status-codes";
import { BulkOrderSource } from "components/BulkOrdersTemplates/interfaces";
import { parseBackendError } from "common/utils/error-utils";

/** Parameters for listing order templates. */
export interface GetTemplatesParams {
    vendor?: string;
    status?: boolean;
    search?: string;
    limit?: number;
    nextToken?: string;
    /** Keyed exact-match filters, e.g. { sku: [...], softwareName: [...] }. Forwarded as sku=/softwareName= query params. */
    filters?: { [key: string]: string[] };
}

/** Parameters for creating a new order template. */
export interface CreateTemplateParams {
    softwareName: string;
    sku: string;
    type: "STANDARD" | "PORTAL";
    cost?: number;
    quantity?: number;
    costCenter?: string;
}

/** Parameters for updating an existing order template. */
export interface UpdateTemplateParams {
    softwareName?: string;
    type?: "STANDARD" | "PORTAL";
    cost?: number;
    quantity?: number;
    costCenter?: string;
    isActive?: boolean;
}

/** Backend list response (cursor-based pagination). */
interface ListTemplatesResponse {
    data: BulkOrderSource[];
    nextToken: string | null;
}

const BULK_ORDER_PATH = "/reseller/templates/orders";

/**
 * Service for Reseller Order Templates CRUD.
 * Calls: GET/POST /reseller/templates/orders, PUT /reseller/templates/orders/{id}
 */
export class BulkOrdersTemplatesService extends BaseService {
    public static instance = new BulkOrdersTemplatesService(API_BASE_URL);

    /**
     * List order templates with filtering and cursor-based pagination.
     * Exact-match filters (sku, softwareName) are forwarded as dedicated GSI-backed query params.
     */
    public async getSources(params: GetTemplatesParams): Promise<{ items: BulkOrderSource[]; nextToken: string | null }> {
        const query = new URLSearchParams();
        if (params.vendor) query.set("vendor", params.vendor);
        if (params.status !== undefined) query.set("status", String(params.status));
        if (params.search) query.set("search", params.search);
        const sku = params.filters?.sku?.[0];
        if (sku) query.set("sku", sku);
        const softwareName = params.filters?.softwareName?.[0];
        if (softwareName) query.set("softwareName", softwareName);
        if (params.limit !== undefined) query.set("limit", String(params.limit));
        if (params.nextToken) query.set("nextToken", params.nextToken);

        const response = await this.fetch(`${BULK_ORDER_PATH}?${query.toString()}`);
        if (response.status !== StatusCodes.OK) {
            await parseBackendError(response);
        }

        const data: ListTemplatesResponse = await response.json();
        return {
            items: data.data,
            nextToken: data.nextToken,
        };
    }

    /**
     * Create a new order template.
     */
    public async createSource(data: CreateTemplateParams): Promise<BulkOrderSource> {
        const response = await this.fetch(BULK_ORDER_PATH, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (response.status !== StatusCodes.CREATED) {
            const error = await response.json().catch(() => ({}));
            throw new Error((error as any).message || `Failed to create template: ${response.status}`);
        }

        const { template } = await response.json();
        return template;
    }

    /**
     * Update an existing order template.
     */
    public async updateSource(id: string, data: UpdateTemplateParams): Promise<BulkOrderSource> {
        const response = await this.fetch(`${BULK_ORDER_PATH}/${encodeURIComponent(id)}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (response.status !== StatusCodes.OK) {
            const error = await response.json().catch(() => ({}));
            throw new Error((error as any).message || `Failed to update template: ${response.status}`);
        }

        const { template } = await response.json();
        return template;
    }
}
